import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, DatePicker, Input, Modal, Popconfirm, Spin, Tag, Typography, message } from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import copyToClipboard from "../../utils/copy";
import { buildTransferUrl } from "../../utils/fileTransferToken";
import { listTransfers, updateTransfer, deleteTransfer } from "../../utils/fileTransferAdminApi";
import { defaultExpiry, formatFileSize } from "../../utils/fileTransferForm";
import { useTranslation } from "../../i18n";
import "./fileTransfer.css";

const { Title, Paragraph, Text } = Typography;

const PRESET_DAYS = [1, 3, 7, 14, 30];
const PRESET_SIZES = [
  { label: "100 MB", value: 100 * 1024 * 1024 },
  { label: "1 GB", value: 1024 * 1024 * 1024 },
  { label: "10 GB", value: 10 * 1024 * 1024 * 1024 },
  { label: "100 GB", value: 100 * 1024 * 1024 * 1024 },
  { label: "1 TB", value: 1024 * 1024 * 1024 * 1024 },
];

function FileTransferManage() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listTransfers();
      setSessions(items);
    } catch {
      message.error(t("fileTransfer.manage.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleCopy = useCallback(async (id) => {
    try {
      await copyToClipboard(buildTransferUrl(id));
      message.success(t("fileTransfer.manage.copied"));
    } catch {
      message.error(t("fileTransfer.manage.copyFailed"));
    }
  }, [t]);

  const handleDelete = useCallback(async (id, fileCount) => {
    try {
      await deleteTransfer(id);
      message.success(t("fileTransfer.manage.deleteDone", { extra: fileCount > 0 ? "，已清理" + fileCount + "个文件" : "" }));
      loadSessions();
    } catch {
      message.error(t("fileTransfer.manage.deleteFailed"));
    }
  }, [loadSessions, t]);

  const openEdit = useCallback((session) => {
    setEditError("");
    setEditing({
      id: session.id,
      title: session.title || "",
      description: session.description || "",
      expiry: session.expiry ? dayjs(session.expiry * 1000) : defaultExpiry(),
      maxFileSize: session.maxFileSize || 10737418240,
      maxFiles: session.maxFiles || 500,
    });
  }, []);

  const handleEditField = useCallback((field, value) => {
    setEditing((prev) => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const applyPreset = useCallback((days) => {
    handleEditField("expiry", dayjs().add(days, "day").hour(23).minute(59).second(59).millisecond(0));
  }, [handleEditField]);

  const applySize = useCallback((size) => {
    handleEditField("maxFileSize", size);
  }, [handleEditField]);

  const handleSave = useCallback(async () => {
    if (!editing) return;
    setEditError("");

    const updates = {};
    if (editing.title !== undefined) updates.title = editing.title;
    if (editing.description !== undefined) updates.description = editing.description;
    if (editing.expiry) updates.expiry = Math.floor(editing.expiry.valueOf() / 1000);
    if (editing.maxFileSize) updates.maxFileSize = editing.maxFileSize;
    if (editing.maxFiles) updates.maxFiles = editing.maxFiles;

    setSaving(true);
    try {
      await updateTransfer(editing.id, updates);
      message.success(t("fileTransfer.manage.saved"));
      setEditing(null);
      loadSessions();
    } catch (err) {
      setEditError(err.message || t("fileTransfer.manage.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [editing, loadSessions, t]);

  return (
    <>
      <PageTitle>
        <Navigation />
      </PageTitle>
      <BackHomeIcon to={PANEL_PATH} />

      <div className="file-transfer-page">
        <div className="custom-h1">
          <span>{t("fileTransfer.manage.title")}</span>
        </div>
        <Paragraph style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
          {t("fileTransfer.manage.subtitle")}
        </Paragraph>

        <div className="file-transfer-admin-section">
          {loading ? (
            <div className="file-transfer-admin-list__loading">
              <Spin size="large" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="file-transfer-admin-list__empty">
              <Paragraph type="secondary" style={{ textAlign: "center" }}>
                {t("fileTransfer.manage.noSessions")}
              </Paragraph>
            </div>
          ) : (
            <div className="file-transfer-admin-list">
              {sessions.map((session) => (
                <div key={session.id} className="file-transfer-admin-list__item">
                  <div className="file-transfer-admin-list__main">
                    <div className="file-transfer-admin-list__title">
                      <span>{session.title || t("fileTransfer.manage.noTitle")}</span>
                      {session.expired && <Tag color="error">{t("fileTransfer.manage.expired")}</Tag>}
                    </div>
                    <div className="file-transfer-admin-list__meta">
                      <Text type="secondary">
                        ID: {session.id} | {t("fileTransfer.session.fileSection")}: {session.fileCount || 0} | {formatFileSize(session.totalBytes || 0)}
                        {" | "}{formatFileSize(session.maxFileSize || 0)}/个，最多 {session.maxFiles || 0} 个
                        {" | "}{dayjs(session.createdAt * 1000).format("YYYY-MM-DD HH:mm")}
                        {" | "}{dayjs(session.expiry * 1000).format("YYYY-MM-DD HH:mm")}
                        {" | "}{session.viewCount} 次
                      </Text>
                    </div>
                  </div>
                  <div className="file-transfer-admin-list__actions">
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(session)}>
                      {t("fileTransfer.manage.edit")}
                    </Button>
                    <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(session.id)}>
                      {t("fileTransfer.admin.copyLink")}
                    </Button>
                    <Popconfirm
                      title={t("fileTransfer.manage.deleteConfirmTitle")}
                      description={t("fileTransfer.manage.deleteConfirmDesc", { count: session.fileCount || 0, size: formatFileSize(session.totalBytes || 0) })}
                      onConfirm={() => handleDelete(session.id, session.fileCount || 0)}
                      okText={t("fileTransfer.manage.deleteOk")}
                      cancelText={t("fileTransfer.manage.deleteCancel")}
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={Boolean(editing)}
        title={t("fileTransfer.manage.editTitle")}
        onCancel={() => !saving && setEditing(null)}
        footer={[
          <Button key="cancel" disabled={saving} onClick={() => setEditing(null)}>{t("fileTransfer.manage.cancel")}</Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>{t("fileTransfer.manage.save")}</Button>,
        ]}
        width={560}
        destroyOnClose
      >
        {editing && (
          <>
            <div className="file-transfer-admin-field">
              <label>{t("fileTransfer.admin.fieldTitle")}</label>
              <Input
                value={editing.title}
                onChange={(e) => handleEditField("title", e.target.value)}
              />
            </div>
            <div className="file-transfer-admin-field">
              <label>{t("fileTransfer.admin.fieldDescription")}</label>
              <Input.TextArea
                value={editing.description}
                onChange={(e) => handleEditField("description", e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </div>
            <div className="file-transfer-admin-field">
              <label>{t("fileTransfer.admin.fieldExpiry")}</label>
              <div className="file-transfer-admin-presets">
                {PRESET_DAYS.map((days) => (
                  <Button key={days} onClick={() => applyPreset(days)}>
                    {days === 1 ? t("fileTransfer.admin.day") : t("fileTransfer.admin.days", { days })}
                  </Button>
                ))}
              </div>
              <DatePicker
                showTime={{ format: "HH:mm:ss" }}
                format="YYYY-MM-DD HH:mm:ss"
                value={editing.expiry}
                onChange={(value) => value && handleEditField("expiry", value)}
                style={{ width: "100%" }}
                inputReadOnly
              />
            </div>
            <div className="file-transfer-admin-field">
              <label>{t("fileTransfer.admin.fieldMaxFileSize")}</label>
              <div className="file-transfer-admin-presets">
                {PRESET_SIZES.map((opt) => (
                  <Button
                    key={opt.value}
                    type={editing.maxFileSize === opt.value ? "primary" : "default"}
                    onClick={() => applySize(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                suffix={t("fileTransfer.admin.bytes")}
                value={editing.maxFileSize}
                onChange={(e) => handleEditField("maxFileSize", parseInt(e.target.value, 10) || 0)}
              />
              <Paragraph type="secondary" style={{ marginTop: "0.3rem", marginBottom: 0, fontSize: "0.82rem" }}>
                {t("fileTransfer.admin.maxFileSizeHint", { size: formatFileSize(editing.maxFileSize) })}
              </Paragraph>
            </div>
            <div className="file-transfer-admin-field">
              <label>{t("fileTransfer.admin.fieldMaxFiles")}</label>
              <Input
                type="number"
                value={editing.maxFiles}
                onChange={(e) => handleEditField("maxFiles", parseInt(e.target.value, 10) || 1)}
                min={1}
                max={10000}
              />
            </div>
            {editError && <Paragraph type="danger">{editError}</Paragraph>}
          </>
        )}
      </Modal>

      <Footer />
    </>
  );
}

export default FileTransferManage;
